import {useTranslation} from '@aboutyou/i18n';
import {cx} from '@linaria/core';
import {styled} from '@linaria/react';
import React, {Ref, useMemo} from 'react';
import {COLORS} from '../../../constants/colors';
import {SPACING} from '../../../constants/sizing';
import {FONT_SIZES, FONT_WEIGHTS} from '../../../constants/typography';
import {Button} from '../Buttons/Button';
import {UnstyledButton} from '../Buttons/UnstyledButton';
import {Icon, IconSize} from '../Icon/Icon';
import {GenericIconName, IconName} from '../Icon/IconName';
import {tabFocusedStyles} from '../TabFocused/tabFocusedStyles';
import {AlertProps, AlertTypes} from './types';

type AlertPropsBase = AlertProps & {
  className?: string;
};

type IconType = {
  asset: IconName;
  size?: keyof typeof IconSize;
  color: string;
};

export type AlertPropsCustom = AlertPropsBase & {
  type: 'CUSTOM';
  customConfig: {
    borderColor: string;
    backgroundColor: string;
    icon?: IconType;
  };
};

type AlertPropsCustomDefault = AlertPropsBase & {
  customConfig?: never;
};

type AlertPropsType = AlertPropsCustom | AlertPropsCustomDefault;

export const Alert = ({
  content,
  showCloseButton,
  type,
  cta,
  'data-testid': dataTestid = 'toast',
  onClickCloseButton,
  className,
  customConfig,
  ref,
}: AlertPropsType & {ref?: Ref<HTMLDivElement>}) => {
  const {t} = useTranslation();

  const iconData: IconType | undefined = useMemo(() => {
    if (type === 'CUSTOM') {
      return customConfig?.icon;
    }

    return {
      asset: TOAST_ICON[type].icon,
      color: TOAST_ICON[type].color,
      size: 'S',
    };
  }, [customConfig, type]);

  return (
    <Gradient
      $borderColor={
        type === 'CUSTOM'
          ? customConfig?.borderColor
          : TOAST_STYLE_TYPE[type].borderColor
      }
      $backgroundColor={
        type === 'CUSTOM'
          ? customConfig?.backgroundColor
          : TOAST_STYLE_TYPE[type].backgroundColor
      }
      data-testid={dataTestid}
      className={cx(className)}
      role="alertdialog"
      aria-live="polite"
      aria-atomic="true"
      aria-labelledby="toast-content"
      ref={ref}
    >
      <InnerWrapper>
        {iconData && (
          <Icon
            asset={iconData.asset}
            fill={iconData.color}
            size={iconData.size}
          />
        )}
        <Content id="toast-content" data-testid="toast-content">
          {typeof content === 'function'
            ? content(onClickCloseButton)
            : content}
        </Content>
        {cta && (
          <CtaButton
            data-testid="toastCta"
            variant="secondary"
            aria-label={undefined}
            href={cta.url}
            onClick={cta.onClick}
          >
            {cta.label}
          </CtaButton>
        )}
        {showCloseButton && onClickCloseButton && (
          <CloseButton
            data-testid="toastCloseButton"
            onClick={onClickCloseButton}
            aria-label={t('Close')}
          >
            <Icon
              asset={GenericIconName.CloseSmall}
              size="S"
              fill={COLORS['Text.Black']}
            />
          </CloseButton>
        )}
      </InnerWrapper>
    </Gradient>
  );
};

const TOAST_ICON: Record<
  Exclude<AlertTypes, 'CUSTOM'>,
  {color: string; icon: IconName}
> = {
  ERROR: {
    icon: GenericIconName.Warning,
    color: COLORS['Text.Black'],
  },
  INFO: {
    icon: GenericIconName.Info,
    color: COLORS['Text.Black'],
  },
  SUCCESS: {
    icon: GenericIconName.AlertSuccess,
    color: COLORS['Text.Black'],
  },
  WARNING: {
    icon: GenericIconName.Warning,
    color: COLORS['Text.Black'],
  },
};

const TOAST_STYLE_TYPE: Record<
  Exclude<AlertTypes, 'CUSTOM'>,
  {
    borderColor: string;
    backgroundColor: string;
  }
> = {
  ERROR: {
    borderColor: COLORS['Alerts.Error.Primary'],
    backgroundColor: COLORS['Alerts.Error.Secondary'],
  },
  INFO: {
    borderColor: COLORS['Alerts.Info.Primary'],
    backgroundColor: COLORS['Alerts.Info.Secondary'],
  },
  SUCCESS: {
    borderColor: COLORS['Alerts.Success.Primary'],
    backgroundColor: COLORS['Alerts.Success.Tertiary'],
  },
  WARNING: {
    borderColor: COLORS['Alerts.Warning.Primary'],
    backgroundColor: COLORS['Alerts.Warning.Secondary'],
  },
};

const Gradient = styled.div<{$backgroundColor?: string; $borderColor?: string}>`
  display: flex;
  font-size: ${FONT_SIZES.px12};
  line-height: 1.5;
  border-radius: 4px;
  padding: ${SPACING['X.Lg']};
  gap: ${SPACING['X.Lg']};
  margin: auto;
  position: relative;
  width: 100%;
  border: 1px solid;

  background-color: ${props =>
    props.$backgroundColor ? `${props.$backgroundColor}` : ''};
  border-color: ${props => (props.$borderColor ? `${props.$borderColor}` : '')};
`;

const InnerWrapper = styled.div`
  display: flex;
  gap: ${SPACING['X.Lg']};
  align-items: center;
  width: 100%;
`;

const CloseButton = styled(UnstyledButton)`
  border: none;
  background-color: transparent;
  margin-left: auto;
  flex-grow: 0;

  &:focus {
    outline: none;
  }

  &:focus-visible {
    ${tabFocusedStyles()}
  }
`;

const Content = styled.span`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  width: 100%;
  font-weight: ${FONT_WEIGHTS.mediumBold};
  font-size: ${FONT_SIZES.px12};
  line-height: 1.5;
`;

const CtaButton = styled(Button)`
  &&& {
    --border-width: 1px;
    --border-color: ${COLORS['Text.Black']};
    background-color: transparent;
    gap: 10px;

    min-width: 80px;
    height: auto;
    width: fit-content;
    font-size: ${FONT_SIZES.px10};
    font-weight: ${FONT_WEIGHTS.mediumBold};
    line-height: 1.6;
    min-height: auto;
    padding: 4px 8px;
  }

  & > span {
    background-color: transparent;
    font-size: inherit;
    font-weight: inherit;
    padding: 0;
  }
`;
